import * as opentype from "opentype.js";
import { getFontAsset } from "@/fonts/assetStore";
import { FONT_REGISTRY } from "@/fonts/constants";
import { resolveClosestFontFile } from "@/fonts/fontRegistry";
import type { FontSourceFile } from "@/fonts/types";
import type { OutlineFontCache, OutlineTextJob, Woff2Decompressor } from "./types";

const outlineFontCache: OutlineFontCache = new Map<string, Promise<opentype.Font>>();

let woff2DecompressorPromise: Promise<Woff2Decompressor | null> | null = null;
let measureCanvasContext: CanvasRenderingContext2D | null = null;

const normalizeFontWeight = (value: string | null): number => {
    if (!value) return 400;

    if (value === "bold") return 700;

    const parsed = Number.parseInt(value, 10);

    return Number.isNaN(parsed) ? 400 : parsed;
};

const normalizeFontStyle = (value: string | null): "normal" | "italic" => {
    return value === "italic" ? "italic" : "normal";
};

/**
 * Подбирает файл шрифта по семейству, ближайшему весу и стилю.
 */
const resolveFontFile = (
    family: string,
    weight: number,
    style: "normal" | "italic",
): FontSourceFile | null => {
    const source = FONT_REGISTRY[family];
    if (!source || source.length === 0) return null;

    return resolveClosestFontFile(source, weight, style);
};

/**
 * Ленивая загрузка декомпрессора для woff2.
 */
const getWoff2Decompressor = async (): Promise<Woff2Decompressor | null> => {
    if (!woff2DecompressorPromise) {
        woff2DecompressorPromise = import("woff2-encoder/decompress")
            .then((mod) => (mod as { default?: unknown }).default ?? mod)
            .then((decompress) => {
                if (typeof decompress === "function") {
                    return decompress as Woff2Decompressor;
                }
                return null;
            })
            .catch(() => null);
    }

    return woff2DecompressorPromise;
};

/**
 * Декодирует woff2-буфер в формат, пригодный для opentype.parse.
 */
const decodeWoff2 = async (buffer: ArrayBuffer): Promise<ArrayBuffer | SharedArrayBuffer> => {
    const decompress = await getWoff2Decompressor();
    if (!decompress) return buffer;

    const result = await decompress(new Uint8Array(buffer));

    return result.buffer.slice(result.byteOffset, result.byteOffset + result.byteLength);
};

/**
 * Загружает и парсит шрифт с кэшированием по URL.
 */
const loadOutlineFont = (fontUrl: string): Promise<opentype.Font> => {
    const cached = outlineFontCache.get(fontUrl);
    if (cached) return cached;

    const promise = (async () => {
        let buffer: ArrayBuffer | SharedArrayBuffer = await getFontAsset(fontUrl);
        if (fontUrl.toLowerCase().endsWith(".woff2")) {
            buffer = await decodeWoff2(buffer);
        }

        return opentype.parse(buffer);
    })();

    outlineFontCache.set(fontUrl, promise);

    return promise;
};

const parseFontSize = (value: string | null): number => {
    if (!value) return 16;

    const parsed = Number.parseFloat(value);

    return Number.isNaN(parsed) ? 16 : parsed;
};

const parseCoord = (value: string | null): number => {
    if (!value) return 0;

    const parsed = Number.parseFloat(value);

    return Number.isNaN(parsed) ? 0 : parsed;
};

const getMeasureContext = (): CanvasRenderingContext2D | null => {
    if (measureCanvasContext) return measureCanvasContext;

    const canvas = document.createElement("canvas");
    measureCanvasContext = canvas.getContext("2d");

    return measureCanvasContext;
};

const measureExpectedTextWidth = (textElement: SVGTextElement, text: string): number | null => {
    const ctx = getMeasureContext();
    if (!ctx) return null;

    const fontStyle = normalizeFontStyle(textElement.getAttribute("font-style"));
    const fontWeight = normalizeFontWeight(textElement.getAttribute("font-weight"));
    const fontSize = parseFontSize(textElement.getAttribute("font-size"));
    const fontFamily = getPrimaryFontFamily(textElement.getAttribute("font-family")) ?? "Arial";
    const fontParts = [
        fontStyle !== "normal" ? fontStyle : "",
        String(fontWeight),
        `${fontSize}px`,
        `"${fontFamily.replace(/['"]/g, "").trim()}"`,
    ].filter(Boolean);

    ctx.font = fontParts.join(" ");

    return ctx.measureText(text).width;
};

const getPrimaryFontFamily = (value: string | null): string | null => {
    if (!value) return null;

    const [primary] = value.split(",");

    return primary?.replace(/['"]/g, "").trim() || null;
};

const computeAnchoredX = (textWidth: number, anchor: string | null, x: number): number => {
    if (!anchor || anchor === "start") return x;

    if (anchor === "middle") return x - textWidth / 2;

    if (anchor === "end") return x - textWidth;

    return x;
};

const copyTextPresentation = (
    source: SVGTextElement,
    target: SVGPathElement,
    extraTransform: string | null = null,
): void => {
    const attrs = ["fill", "fill-opacity", "opacity", "stroke", "stroke-width", "stroke-opacity"];
    for (const attr of attrs) {
        const value = source.getAttribute(attr);
        if (value) target.setAttribute(attr, value);
    }

    const sourceTransform = source.getAttribute("transform");
    const transform = [sourceTransform, extraTransform].filter(Boolean).join(" ");
    if (transform) {
        target.setAttribute("transform", transform);
    }
};

/**
 * Нормализует font-family для экспорта в PDF.
 */
export function normalizeFontFamily(textElement: SVGTextElement): void {
    const primaryFont = getPrimaryFontFamily(textElement.getAttribute("font-family"));
    if (!primaryFont) return;

    if (primaryFont === "serif") {
        // В некоторых шаблонах используется serif. Ближе к нему Times New Roman
        textElement.setAttribute("font-family", "Times New Roman");
        return;
    }

    if (primaryFont === "sans-serif") {
        // Остальной текст на доске обычно sans-serif. Ближе к нему Arial
        textElement.setAttribute("font-family", "Arial");
        return;
    }

    textElement.setAttribute("font-family", primaryFont);

    // Нормализуем вес/стиль до реально доступного варианта семейства,
    // чтобы SVG-разметка и outline-конвертация использовали одинаковые метрики.
    const targetWeight = normalizeFontWeight(textElement.getAttribute("font-weight"));
    const targetStyle = normalizeFontStyle(textElement.getAttribute("font-style"));
    const fontFile = resolveFontFile(primaryFont, targetWeight, targetStyle);
    if (!fontFile) return;

    textElement.setAttribute("font-weight", String(fontFile.weight));
    textElement.setAttribute("font-style", fontFile.style);
}

/**
 * Конвертирует все text-элементы SVG в path-контуры.
 */
export async function convertTextToOutline(svgElement: HTMLElement): Promise<void> {
    const textElements = Array.from(svgElement.querySelectorAll("text"));
    const jobs: OutlineTextJob[] = [];

    for (const node of textElements) {
        const textElement = node as SVGTextElement;
        const text = textElement.textContent ?? "";
        if (!text.trim()) continue;

        const fontFamily = getPrimaryFontFamily(textElement.getAttribute("font-family")) ?? "Arial";
        const fontWeight = normalizeFontWeight(textElement.getAttribute("font-weight"));
        const fontStyle = normalizeFontStyle(textElement.getAttribute("font-style"));
        const fontSize = parseFontSize(textElement.getAttribute("font-size"));
        const anchor = textElement.getAttribute("text-anchor");

        const fontFile = resolveFontFile(fontFamily, fontWeight, fontStyle);
        if (!fontFile) continue;

        jobs.push({
            textElement,
            text,
            fontSize,
            anchor,
            x: parseCoord(textElement.getAttribute("x")),
            y: parseCoord(textElement.getAttribute("y")),
            fontUrl: fontFile.url,
        });
    }

    const uniqueUrls = Array.from(new Set(jobs.map((job) => job.fontUrl)));
    const preloadResults = await Promise.allSettled(uniqueUrls.map((url) => loadOutlineFont(url)));

    const loadedFontsByUrl = new Map<string, opentype.Font>();
    for (let i = 0; i < preloadResults.length; i++) {
        const result = preloadResults[i];
        const url = uniqueUrls[i];
        if (!url || result.status !== "fulfilled") continue;
        loadedFontsByUrl.set(url, result.value);
    }

    for (const job of jobs) {
        const font = loadedFontsByUrl.get(job.fontUrl);
        if (!font) continue;

        const outlineWidth = font.getAdvanceWidth(job.text, job.fontSize);
        const expectedWidth = measureExpectedTextWidth(job.textElement, job.text) ?? outlineWidth;
        const x = computeAnchoredX(expectedWidth, job.anchor, job.x);
        const pathData = font.getPath(job.text, x, job.y, job.fontSize).toPathData(2);
        const scaleX = outlineWidth > 0 ? expectedWidth / outlineWidth : 1;

        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", pathData);
        const extraTransform =
            Number.isFinite(scaleX) && Math.abs(scaleX - 1) > 0.001
                ? `matrix(${scaleX} 0 0 1 ${x * (1 - scaleX)} 0)`
                : null;
        copyTextPresentation(job.textElement, path, extraTransform);

        const parent = job.textElement.parentNode;
        if (parent) {
            parent.replaceChild(path, job.textElement);
        }
    }
}

export function clearOutlineFontCache(): void {
    outlineFontCache.clear();
}
